import { msg } from "@lingui/core/macro";
import { Show } from "solid-js";
import { forceReload, updateStalled } from "@fleet/version";
import { useI18n } from "@/providers/i18n-provider";

/**
 * The degraded half of the version-compatibility feature.
 *
 * When the front falls below the API's `min_supported` contract version it
 * updates itself silently — the user sees a reload and nothing else. This banner
 * only appears once that has FAILED: the attempt budget ran out (the CDN edge
 * kept serving the stale bundle) or the attempts could not be counted at all
 * (storage blocked). At that point we stop reloading — an unbounded reload loop
 * leaves the app permanently unusable, which is worse than the original problem
 * — and hand the user the one action that can still work.
 *
 * By design this is the ONLY user-visible surface of the whole feature.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export function UpdateStalledBanner() {
  const { t } = useI18n();

  return (
    <Show when={updateStalled()}>
      <div
        role="status"
        aria-live="polite"
        data-testid="update-stalled-banner"
        class="fixed inset-x-0 bottom-0 z-50 flex flex-wrap items-center justify-center gap-3 bg-amber-500/95 px-4 py-3 text-sm text-amber-950 shadow-lg"
      >
        <span>{t(msg`This app version is out of date and could not update itself.`)}</span>
        <button
          type="button"
          data-testid="update-stalled-reload"
          class="rounded-md bg-amber-950 px-3 py-1 font-medium text-amber-50 transition-opacity hover:opacity-90"
          onClick={forceReload}
        >
          {t(msg`Reload now`)}
        </button>
      </div>
    </Show>
  );
}
